import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useTranslation } from 'react-i18next';
import { REQUEST_DELETE_ACCOUNT } from 'src/apiService/apiDeclaration';
import { showErrorTost, showSuccessTost } from 'src/constants';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const DeleteAccount = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (!emailRegex.test(e.target.value)) {
      setEmailError(t('deleteAccount.invalidEmail'));
    } else {
      setEmailError('');
    }
  };

  const handleOpen = () => {
    if (!email || emailError) return;
    setOpen(true);
  };
  const handleClose = () => setOpen(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await REQUEST_DELETE_ACCOUNT({ email });
      setDeleting(false);
      setOpen(false);
      showSuccessTost(t('deleteAccount.successMessage'));
      window.location.href = '/'; // Example: redirect to home
    } catch (error) {
      setDeleting(false);
      setOpen(false);
      showErrorTost(error.message || t('deleteAccount.deleteFailed'));
    }
  };

  return (
    <Box minHeight="100vh" display="flex" alignItems="center" justifyContent="center" bgcolor="#f7f8fa">
      <Paper elevation={3} sx={{ p: 4, borderRadius: 3, maxWidth: 420, width: '100%' }}>
        <Stack alignItems="center" spacing={2}>
          <Box
            sx={{
              bgcolor: '#ffeaea',
              borderRadius: '50%',
              width: 56,
              height: 56,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 1,
            }}
          >
            <DeleteOutlineIcon sx={{ color: '#ff4842', fontSize: 32 }} />
          </Box>
          <Typography variant="h5" fontWeight={700} align="center">
            {t('deleteAccountPage.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center">
            {t('deleteAccountPage.description')}
          </Typography>
          <Box width="100%" mt={2} mb={1}>
            <Stack
              direction="row"
              alignItems="flex-start"
              spacing={1}
              sx={{ bgcolor: '#fff6ea', p: 2, borderRadius: 2 }}
            >
              <WarningAmberIcon sx={{ color: '#ffb300', mt: '2px' }} />
              <Box>
                <Typography variant="subtitle2" color="#ffb300" fontWeight={700}>
                  {t('deleteAccountPage.warningTitle')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('deleteAccountPage.warningText')}
                </Typography>
              </Box>
            </Stack>
          </Box>
          <Divider sx={{ width: '100%', my: 1 }} />
          <TextField
            fullWidth
            label={t('deleteAccount.enterEmail')}
            placeholder={t('deleteAccount.emailPlaceholder')}
            value={email}
            onChange={handleEmailChange}
            error={!!emailError}
            helperText={emailError}
            sx={{ mt: 2 }}
            autoComplete="email"
          />
          <Button
            variant="contained"
            color="error"
            fullWidth
            sx={{ mt: 3, fontWeight: 700, fontSize: 16, py: 1.2 }}
            onClick={handleOpen}
            disabled={!email || !!emailError}
          >
            {t('deleteAccountPage.continueButton')}
          </Button>
        </Stack>
      </Paper>
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>{t('deleteAccount.confirmDeleteTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('deleteAccount.confirmDeleteMessage')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={deleting}>
            {t('deleteAccount.cancel')}
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? t('deleteAccount.deleting') : t('deleteAccount.confirmDelete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeleteAccount;
